import {
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { Router, RouterModule } from '@angular/router';
import { UserService } from '@api-client';
import {
  AlertService,
  Button,
  Checkbox,
  InputEmail,
  InputPassword,
  LocalStorageService,
  ModalService,
  Theme,
  ThemeStore,
  ToastService,
} from '@client';
import { CLIENT_ACCESS_TOKEN_KEY, fnGetKeyByValue, MemberRole } from '@common';
import { injectQueryParams } from 'ngxtension/inject-query-params';
import { UserStore } from 'apps/client/src/app/stores/user.store';
import { CommonModule } from '@angular/common';
import {
  booleanAttribute,
  Component,
  inject,
  input,
  signal,
} from '@angular/core';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-sign-in',
  imports: [
    CommonModule,
    Button,
    FormsModule,
    ReactiveFormsModule,
    InputEmail,
    InputPassword,
    Checkbox,
    RouterModule,
    TranslatePipe,
  ],
  templateUrl: './sign-in.compoent.html',
  styleUrl: './sign-in.compoent.scss',
})
export class SignInCompoent {
  readonly themeStore = inject(ThemeStore);
  readonly modalService = inject(ModalService);
  private originalTheme: Theme;

  readonly redirect = injectQueryParams('redirect');

  modal = input<boolean, string | boolean>(false, {
    transform: booleanAttribute,
  });

  router = inject(Router);
  userService = inject(UserService);
  localStorageService = inject(LocalStorageService);
  toastService = inject(ToastService);
  alertService = inject(AlertService);
  userStore = inject(UserStore);
  slideEnabled = signal(true);
  accessTokenKey = CLIENT_ACCESS_TOKEN_KEY;
  type = signal<string>('');
  translate = inject(TranslateService);

  form = new FormGroup({
    email: new FormControl('', {
      validators: [Validators.required],
      nonNullable: true,
    }),
    password: new FormControl('', {
      validators: [Validators.required],
      nonNullable: true,
    }),
  });

  constructor() {
    this.originalTheme = this.themeStore.theme() ?? ({} as Theme);
    this.themeStore.setTheme('light');
  }

  login() {
    if (this.form.invalid) {
      return;
    }
    const { email, password } = this.form.getRawValue();

    this.userService
      .userControllerLogin({
        body: {
          email,
          password,
        },
      })
      .subscribe({
        next: async (res) => {
          await this.localStorageService.set(
            this.accessTokenKey,
            res.accessToken,
          );

          this.userStore.fetchUser().then(
            () => {
              this.toastService.success('로그인 되었습니다.');
              this.themeStore.setTheme('dark');

              if (this.modal()) {
                this.modalService.close();
                this.themeStore.setTheme('dark');
              } else {
                this.router.navigateByUrl('/');
                this.themeStore.setTheme('dark');
              }
            },
            (error) => {
              if (error.statusCode === 401) {
                this.alertBlock();
              } else {
                this.toastService.error(error.message);
              }
            },
          );
        },
        error: (error) => {
          this.toastService.error(error.message);
        },
      });
  }

  findEmail() {
    if (this.modal()) {
      this.modalService.close();
    }
    this.router.navigateByUrl('/auth/find-email', {
      state: { theme: 'light' },
    });
  }

  findPassword() {
    if (this.modal()) {
      this.modalService.close();
    }
    this.router.navigateByUrl('/auth/find-password', {
      state: { theme: 'light' },
    });
  }

  singUp() {
    if (this.modal()) {
      this.modalService.close();
    }
    this.router.navigateByUrl('/auth/sign-up', { state: { theme: 'light' } });
  }

  alertBlock() {
    this.alertService.open({
      type: 'danger',
      icon: {
        name: 'solar:danger-circle-bold',
        color: 'red',
      },
      title: this.translate.instant('alert.block.title'),
      content: this.translate.instant('alert.block.content'),
      buttons: {
        confirm: {
          hidden: true,
        },
        cancel: {
          text: this.translate.instant('common.close'),
          color: 'gray',
        },
      },
    });
  }
}
